import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Hidden,
  Checkbox,
  FormLabel,
  Typography,
  makeStyles,
  withStyles,
  FormControl,
  DialogActions,
  DialogContent,
  FormControlLabel,
} from '@material-ui/core';
import MuiExpansionPanel from '@material-ui/core/ExpansionPanel';
import MuiExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import MuiExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import { 
  ideaCreateActions, 
  multiAreaCascadeActions,
  ideaCreateDialogsActions,
  personalizedClassificationActions,
} from '../../actions';
import config from '../../config';
import { dialogCss } from './dialogCss';
import { returnPage } from '../../helpers';
import { useWindowSize } from '../../hooks';
import { DialogCreateIdea } from './DialogCreateIdea';
import { DialogCustomTitle } from './DialogCustomTitle';

const ExpansionPanel = withStyles(theme => ({
  root: {
    width: '100%',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&$expanded': { margin: 'auto', },
  },
  expanded: {},
})) (MuiExpansionPanel);

const ExpansionPanelSummary = withStyles(theme => ({
  root: {
    padding: 0,
    height: 64,
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {}
})) (MuiExpansionPanelSummary);

const ExpansionPanelDetails = withStyles(theme => ({
  root: {
    fontWeight: 'none',
    padding: theme.spacing(1, 2, 1, 3),
  }
})) (MuiExpansionPanelDetails);

const useStyles = makeStyles((theme) => ({
  dividerBottom: {
    borderBottom: 'none',
  },
  name: {
    fontSize: 16,
    color: theme.palette.neutrals.low.main,
  },
  description: {
    fontSize: 13,
  },
}));

export function DialogClassification() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [width] = useWindowSize();
  const dialogClasses = dialogCss();
  const { handleSubmit, register } = useForm();
  const idea = useSelector((state) => state.ideaCreate);
  const { dialogClassification, from } = useSelector((state) => state.ideaCreateDialogs);
  const { personalizedClassifications } = useSelector((state) => state.ideaCreate);
  const { personalizedClassification } = useSelector((state) => state.personalizedClassification);

  useEffect(() => {
    dispatch(personalizedClassificationActions.getPersonalizedClassification());
  }, [dispatch]);

  function onSubmit() {
    dispatch(ideaCreateDialogsActions.openDialogSendIdea());
  }

  function createDraft() {
    dispatch(ideaCreateActions.createDraft(idea, t('ideia_salva_em_rascunhos')));
  }

  function selectedClassification(itemPersonalizedClassification) {
    const itemChecked = isItemCheckedClassification(itemPersonalizedClassification)

    if(!itemChecked) {
      dispatch(ideaCreateActions.setClassification(itemPersonalizedClassification));
    } else {
      dispatch(ideaCreateActions.dropClassification(itemPersonalizedClassification._id));
    }
  }

  function isItemCheckedClassification(itemPersonalizedClassification) {
    const isChecked = personalizedClassifications.some((item) => item._id === itemPersonalizedClassification._id);

    return !!isChecked;
  }

  function previous() {
    returnPage(dispatch, from);
  }

  function close() {
    if (width < config.RESPONSIVE_MOBILE) {
      dispatch(ideaCreateDialogsActions.openDialogClose());
    } else {
      dispatch(ideaCreateActions.clearAll());
      dispatch(ideaCreateDialogsActions.close());
    }
    dispatch(multiAreaCascadeActions.clearAll());
  }

  return(
    <DialogCreateIdea open={dialogClassification}>
      <DialogCustomTitle id="customized-dialog-title" onClose={close}>
        {t('como_sua_ideia_pode_ser_classificada')}
      </DialogCustomTitle>
      <DialogContent dividers className={classes.dividerBottom}>
        <FormControl component="fieldset" style={{ width: '100%' }}>
          <FormLabel component="legend" className={dialogClasses.subtitle}>
            {t('selecione_uma_ou_mais_categorias_que_se_adequam_a_proposta_da_ideia')}
          </FormLabel>
          
          {personalizedClassification?.map((item, index) => (
            <ExpansionPanel key={index}>
              <ExpansionPanelSummary
                classes={{root:"root", expanded: 'expanded'}}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <FormControlLabel
                  key={item._id}
                  value={item._id}
                  label={item.name}
                  inputRef={register}
                  control={<Checkbox />}
                  className={classes.name}
                  onChange={() => selectedClassification(item)}
                  checked={isItemCheckedClassification(item)}
                />
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Typography className={classes.description}>
                  {item.description}
                </Typography>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ))}

        </FormControl>
      </DialogContent>
      <DialogActions className={dialogClasses.containerAction}>
        <Box width="100%" display="flex" justifyContent="space-between">
          <Box>
            {from && (
              <Button
                color="primary"
                onClick={previous}
                startIcon={<ArrowBackIcon />}
                className={dialogClasses.button}
              >
                {t('voltar')}
              </Button>
            )}
          </Box>
          <Box>
            <Hidden smDown>
              <Button
                className={dialogClasses.button}
                variant="outlined"
                color="primary"
                style={{ marginRight: 8 }}
                onClick={() => {
                  createDraft();
                }}
              >
                {t('salvar_rascunho')}
              </Button>
            </Hidden>
            <Button
              className={dialogClasses.button}
              variant="contained"
              type="submit"
              color="primary"
              startIcon={<ArrowForwardIcon />}
              onClick={handleSubmit(onSubmit)}
            >
              {t('avancar')}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </DialogCreateIdea>
  );
}